/**
 * Lesson 2: Understanding Prices
 *
 * Explains the price-probability relationship in detail,
 * and introduces Yes vs No shares.
 */

import type { Lesson } from '../manager'

export const lesson2: Lesson = {
  id: 'lesson-2',
  title: 'Understanding Prices',
  description: 'Learn how to read prices and understand Yes vs No shares',
  order: 2,
  prerequisites: ['lesson-1'],
  completionCriteria: { type: 'quiz-pass', minQuizScore: 70 },
  objectives: [
    'Convert between prices and probabilities',
    'Understand Yes and No shares',
    'Calculate potential profit and loss',
  ],
  content: [
    // Page 1: Reading Prices
    {
      type: 'text',
      data: {
        markdown: `# Understanding Prices

Now that you know prices represent probabilities, let's dig deeper into how to read and use them.

## Converting Prices to Probabilities

For this curriculum's simplified binary model, use this rule of thumb:
- **Price × 100 = Probability percentage**

| Price | Calculation | Probability |
|-------|-------------|-------------|
| $0.25 | 0.25 × 100 | 25% |
| $0.50 | 0.50 × 100 | 50% |
| $0.73 | 0.73 × 100 | 73% |
| $0.91 | 0.91 × 100 | 91% |

When you see a price of **$0.65**, just think "**about 65% chance**."`,
        visualizationId: 'probability-display',
      },
    },

    // Page 2: Yes and No Shares
    {
      type: 'text',
      data: {
        markdown: `## Yes and No Shares

In a binary market, every question has two outcomes: **Yes** and **No**.

You can buy shares in either outcome:
- **Yes shares** pay $1.00 if the event **happens**
- **No shares** pay $1.00 if the event **doesn't happen**

### The Key Rule

**Yes price + No price ≈ $1.00**

If Yes shares cost $0.70, then No shares cost about $0.30.

Why? Because one of them MUST pay out $1.00! In practice, fees or the bid-ask spread can cause small deviations from exactly $1.00.

### Example: "Will Team A win?"

| Outcome | Price | Probability |
|---------|-------|-------------|
| Yes (Team A wins) | $0.60 | 60% |
| No (Team A loses) | $0.40 | 40% |
| **Total** | **$1.00** | **100%** |

If you think Team A has a **better** than 60% chance, buy Yes shares.
If you think they have a **worse** than 60% chance, buy No shares.`,
      },
    },

    // Page 3: Profit and Loss
    {
      type: 'text',
      data: {
        markdown: `## Calculating Your Profit

When you buy shares, your potential profit depends on whether you're right.

### Formula

- **If you win**: Profit = $1.00 - Price paid
- **If you lose**: Loss = Price paid

### Example: Buying Yes at $0.70

You buy 10 Yes shares at $0.70 each.
- **Cost**: 10 × $0.70 = **$7.00**

**If Yes wins:**
- Payout: 10 × $1.00 = $10.00
- **Profit: $10.00 - $7.00 = $3.00** ✅

**If No wins:**
- Payout: $0.00
- **Loss: $7.00** ❌

### The Risk-Reward Tradeoff

| Price | Risk (if wrong) | Reward (if right) |
|-------|-----------------|-------------------|
| $0.20 | Lose $0.20 | Win $0.80 |
| $0.50 | Lose $0.50 | Win $0.50 |
| $0.80 | Lose $0.80 | Win $0.20 |

Cheap shares have more upside but are less likely to win.
Expensive shares are safer but offer less profit.`,
      },
    },

    // Page 4: Interactive concept
    {
      type: 'text',
      data: {
        markdown: `## Practice: Interpreting Prices

Look at the probability display above. It shows the current market price and what it means.

**Key questions to ask yourself:**
1. What does this price mean as a probability?
2. Do I think the true probability is higher or lower?
3. If I'm right, how much could I make?

### When to Buy Yes
- You think the event is **more likely** than the price suggests
- Example: Price is $0.40 but you believe there's a 60% chance

### When to Buy No
- You think the event is **less likely** than the price suggests
- Example: Price is $0.70 but you believe there's only a 50% chance

> **Tip**: You don't have to trade! If you're unsure, it's okay to just watch and learn. Remember, the market price reflects the beliefs of many traders—if your estimate differs, consider why before trading.`,
        visualizationId: 'probability-display',
      },
    },

    // Quiz 1
    {
      type: 'quiz',
      data: {
        question: 'If Yes shares are priced at $0.45, what should No shares cost (approximately)?',
        options: [
          { id: 'a', text: '$0.45' },
          { id: 'b', text: '$0.55' },
          { id: 'c', text: '$1.00' },
          { id: 'd', text: '$0.90' },
        ],
        correctOptionId: 'b',
        explanation:
          'Yes price + No price ≈ $1.00. If Yes costs $0.45, then No costs about $1.00 - $0.45 = $0.55. This is because exactly one outcome will happen and pay $1.00.',
      },
    },

    // Quiz 2
    {
      type: 'quiz',
      data: {
        question: 'You buy 5 Yes shares at $0.60 each. If the event happens, what is your profit?',
        options: [
          { id: 'a', text: '$3.00 profit' },
          { id: 'b', text: '$5.00 profit' },
          { id: 'c', text: '$2.00 profit' },
          { id: 'd', text: '$0.60 profit' },
        ],
        correctOptionId: 'c',
        explanation:
          'Cost: 5 × $0.60 = $3.00. If you win, payout: 5 × $1.00 = $5.00. Profit: $5.00 - $3.00 = $2.00. Each share you bought gives you $0.40 profit ($1.00 - $0.60).',
      },
    },

    // Quiz 3
    {
      type: 'quiz',
      data: {
        question: 'The market prices Yes at $0.30. You believe the true probability is 50%. What should you do?',
        options: [
          { id: 'a', text: 'Buy No shares' },
          { id: 'b', text: 'Buy Yes shares' },
          { id: 'c', text: 'The market is always right, do nothing' },
          { id: 'd', text: 'Wait for the price to reach $0.50' },
        ],
        correctOptionId: 'b',
        explanation:
          'If you believe the true probability is 50% but the market says 30%, Yes shares look underpriced. Buying Yes has positive expected value if your estimate is correct. But remember—the market may include information you don\'t have, so consider why the price might be where it is.',
      },
    },
  ],
}
